
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  EscalationInterface,
  EscalationRepositoryInterface,
  EscalationResponseInterface,
} from '@/services/QA/escalation/interfaces';
import { DataTableHeader } from 'vuetify';
import { ChatInterface, ChatsWSMessageInterface } from '@/services/QA/chat/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import { TicketInterface, TicketsWSMessageInterface } from '@/services/QA/ticket/interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'escalationRepository',
    'userRepository',
    'profileRepository',
  ],
  sockets: {
    chatsData(msg: ChatsWSMessageInterface): void {
      this.$emit('updateChats', msg.chats);
    },
    ticketsData(msg: TicketsWSMessageInterface): void {
      this.$emit('ticketsUpdate', msg.tickets);
    },
  },
})
export default class AllEscalations extends Vue {
  private escalations: EscalationInterface[] = [];
  private loadedEscalations: EscalationInterface[] = [];
  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};

  private showReviewed: boolean = false;
  private totalItems: number = 0;
  private currentPage: number = 1;
  private itemsPerPage: number = 10;

  private loading: boolean = true;
  private responseReceived: boolean = false;
  private chatsMessageReceived: boolean = false;
  private ticketsMessageReceived: boolean = false;

  private search: string = '';
  private headers: DataTableHeader[] = [
    { text: 'Status', value: 'status' },
    { text: 'Type', value: 'type', sortable: false },
    { text: 'Resource ID', value: 'resource', sortable: false },
    { text: 'External', value: 'external', sortable: false },
    { text: 'Date & Time', value: 'dateTime', sortable: false },
    { text: 'Client', value: 'client', sortable: false },
    { text: 'Client Logged', value: 'logged', sortable: false },
    { text: 'Escalated On', value: 'timestamp' },
    { text: 'Escalated To', value: 'escalatedTo', sortable: false },
    { text: 'Escalated From', value: 'escalatedFromUserId', sortable: false },
    { text: 'Priority', value: 'priority' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  private sortBy: string = 'timestamp';
  private sortDesc: boolean = true;
  private options: { sortBy: string[], sortDesc: boolean[] } = { sortBy: [this.sortBy], sortDesc: [this.sortDesc] };

  private escalationRepository!: EscalationRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private profileRepository!: ProfileRepositoryInterface;

  public async beforeCreate(): Promise<void> {
    this.$on('updateChats', async (chats: ChatInterface[]) => {
      this.chatsMessageReceived = true;
      await this.assignChatsData(chats);
    });

    this.$on('ticketsUpdate', async (tickets: TicketInterface[]) => {
      this.ticketsMessageReceived = true;
      await this.assignTicketsData(tickets);
    });
  }

  public async created(): Promise<void> {
    this.showReviewed = this.$store.getters.showReviewedEscalations;
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    this.loading = true;
    this.responseReceived = false;
    this.chatsMessageReceived = false;
    this.ticketsMessageReceived = false;
    this.escalations = [];
    const [escalations, totalItems] = await this.escalationRepository
      .getAllEscalations(this.showReviewed, this.currentPage, this.sortBy, this.sortDesc);
    this.totalItems = totalItems;
    this.responseReceived = true;
    this.assignEscalationsData(escalations);
  }

  private async assignTicketsData(tickets: TicketInterface[]): Promise<void> {
    if (!this.responseReceived) {
      setTimeout(() => this.assignTicketsData(tickets), 100);
      return;
    }

    tickets.forEach((ticket) => {
      const index = this.escalations.findIndex(
        (escalation) => !!escalation.ticketEscalation && escalation.ticketEscalation.ticketId === ticket.id,
      );
      if (index === -1) {
        return;
      }

      Object.assign(this.escalations[index].ticketEscalation.ticket, ticket);
    });

    await this.getUsersInfo();
  }

  private async assignChatsData(chats: ChatInterface[]): Promise<void> {
    if (!this.responseReceived) {
      setTimeout(() => this.assignChatsData(chats), 100);
      return;
    }

    chats.forEach((chat) => {
      const index = this.escalations.findIndex(
        (escalation) => !!escalation.chatEscalation && escalation.chatEscalation.chatId === chat.id,
      );
      if (index === -1) {
        return;
      }

      Object.assign(this.escalations[index].chatEscalation.chat, chat);
    });

    await this.getUsersInfo();
  }

  private assignEscalationsData(escalations: EscalationInterface[]): void {
    escalations.forEach((escalation) => {
      this.escalations.push(escalation);
    });
  }

  private async changePage(): Promise<void> {
    if (this.currentPage < 1 || this.currentPage > Math.ceil(this.totalItems / this.itemsPerPage)) {
      return;
    }
    this.loading = true;
    if (this.search.trim() !== '') {
      await this.searchEscalations();
    } else {
      await this.initialize();
    }
  }

  private async searchEscalations(): Promise<void> {
    this.loading = true;
    this.responseReceived = false;
    this.ticketsMessageReceived = false;
    this.chatsMessageReceived = false;
    this.escalations = [];
    let search = this.search.trim();

    if (search === '') {
      await this.initialize();
      return;
    }

    Object.values(this.usersMap).forEach((user) => {
      if (user.email.includes(search)) {
        search = user.id;
      }
    });

    const [escalations, totalItems] = await this.escalationRepository
      .searchEscalations(this.currentPage, search.toLowerCase(), this.sortBy, this.sortDesc);
    this.totalItems = totalItems;
    this.responseReceived = true;
    this.assignEscalationsData(escalations);
  }

  private async getUsersInfo(): Promise<void> {
    const usersIds: string[] = [];

    this.escalations.forEach((escalation) => {
      if (usersIds.indexOf(escalation.escalatedFromUserId) === -1) {
        usersIds.push(escalation.escalatedFromUserId);
      }

      if (escalation.departmentManager && usersIds.indexOf(escalation.departmentManager) === -1) {
        usersIds.push(escalation.departmentManager);
      }

      if (escalation.shiftManager && usersIds.indexOf(escalation.shiftManager) === -1) {
        usersIds.push(escalation.shiftManager);
      }
    });


    const users = await this.userRepository.getUsersByIds(usersIds);

    users.forEach((user) => {
      this.usersMap[user.id] = user;
    });

    if (this.responseReceived) {
      this.loadedEscalations = this.escalations;
      this.loading = false;
      this.$forceUpdate();
    }
  }

  private async saveReviewedChoice(): Promise<void> {
    await this.profileRepository.updateProfileConfig(
      this.$store.getters.profileId,
      { showReviewedEscalations: this.showReviewed },
    );
  }

  @Watch('options')
  private async watchOptions(): Promise<void> {
    if (this.loading) {
      return;
    }

    const sortBy = this.options.sortBy[0];
    const sortDesc = this.options.sortDesc[0];

    if (!sortBy && !sortDesc) {
      this.sortBy = 'timestamp';
      this.sortDesc = true;
    } else {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    }

    this.loading = true;
    if (this.search.trim() !== '') {
      await this.searchEscalations();
    } else {
      await this.initialize();
    }
  }
}
